import { Component, inject, OnInit, signal, DestroyRef } from '@angular/core';
import { MenuComponent } from '@components/menu/menu.component';
import { selectMenuVisible } from '@store/selectors/component.selectors';
import { selectServiceStatusError, selectServiceStatusIsLoading } from '@store/selectors/service-status.selectors';
import { filter, Observable } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { Translation } from '@modules/shared/i18n/translation.interface';
import { TranslationService } from '@modules/shared/services/translation.service';
import { RouterOutlet } from '@angular/router';
import { AsyncPipe, NgClass } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Actions } from '@ngrx/effects';
import { ComponentsActions, toggleMenu } from '@store/actions/component.actions';
import { initApp } from '@store/actions/app.actions';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fetchCompanies } from '@store/actions/company.actions';
import { TopbarComponent } from '@components/topbar/topbar.component';

import { AppButtonComponent } from '@components/wrappers/button/button.component';

@Component({
  selector: 'app-root',
  imports: [
    MenuComponent,
    TopbarComponent,
    RouterOutlet,
    AsyncPipe,
    ToastModule,
    NgClass,
    AppButtonComponent,
    ConfirmPopupModule,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  providers: [MessageService, ConfirmationService]
})
export class AppComponent implements OnInit {
  protected readonly translationService = inject(TranslationService);
  private readonly messageService = inject(MessageService);
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  protected visible$ = this.store.pipe(select(selectMenuVisible));
  private readonly oidcSecurityService = inject(OidcSecurityService);
  public error$: Observable<string> = this.store.select(selectServiceStatusError);
  public isLoading$: Observable<boolean> = this.store.select(selectServiceStatusIsLoading);
  protected auth$: Observable<LoginResponse> | undefined;
  public translation!: Translation;
  public translation$: Observable<Translation>;
  protected readonly isAuthenticated = signal(false);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.translation$ = this.translationService.getTranslation();
    this.store.dispatch(initApp());
    this.translation$
      .pipe(
        filter(val => val !== undefined),
        takeUntilDestroyed()
      ).subscribe(translation => {
        this.translation = translation;
      });

    this.actions$.pipe(
      takeUntilDestroyed()
    ).subscribe(action => {
      switch (action.type) {
        case ComponentsActions.SHOW_SUCCESS_MESSAGE:
          this.messageService.add({
            severity: 'success',
            summary: action.payload.title || this.translation.general.successTitle,
            detail: action.payload.detail
          });
          break;
        case ComponentsActions.SHOW_ERROR_MESSAGE:
          this.messageService.add({
            severity: 'error',
            summary: action.payload.title || this.translation.general.errorTitle,
            detail: action.payload.detail
          });
          break;
      }
    });
  }

  ngOnInit(): void {
    this.auth$ = this.oidcSecurityService.checkAuth();
    this.auth$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(auth => {
      if (auth.isAuthenticated) {
        this.isAuthenticated.set(true);
        this.store.dispatch(fetchCompanies());
      }
    });
  }

  protected toggleMenu() {
    this.store.dispatch(toggleMenu());
  }

  login() {
    this.oidcSecurityService.authorize();
  }

  logout() {
    this.oidcSecurityService.logoff();
  }

}
